import { WorkerAutoAssignmentPolicySettings } from '@nexelem/api';
import z from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  DEFAULT_RANKING_CONFIG,
  FALLBACK_AUTO_ASSIGN_DEFAULTS,
  ResolvedPolicyConfig,
  WorkerAssignmentRankingConfig,
  WorkerAssignmentRankingCriterion,
  historicalAssignmentsLookbackMonthsSchema,
  minAvailabilityCoveragePercentSchema,
  minOverlapsForPairPerMonthSchema,
  resolvePolicyConfig,
} from './autoAssignmentConfig';
import { useAutoAssignDefaults } from './useAutoAssignDefaults';

// Out-of-range persisted fields are dropped (`catch(undefined)`), so they fall back to the server defaults.
const persistedStateSchema = z.object({
  policyOverrides: z
    .object({
      requireHistoricalAssignment: z.boolean().optional().catch(undefined),
      minAvailabilityCoveragePercent: minAvailabilityCoveragePercentSchema.optional().catch(undefined),
      historicalAssignmentsLookbackMonths: historicalAssignmentsLookbackMonthsSchema.optional().catch(undefined),
      minOverlapsForPairPerMonth: minOverlapsForPairPerMonthSchema.optional().catch(undefined),
    })
    .nullable(),
  rankingConfig: z.object({
    criteria: z
      .array(
        z.object({
          criterion: z.nativeEnum(WorkerAssignmentRankingCriterion),
          enabled: z.boolean(),
        }),
      )
      .min(1),
  }),
});

// Criteria introduced after the user saved their preferences are appended with default settings.
const withNewCriteriaAppended = (stored: WorkerAssignmentRankingConfig): WorkerAssignmentRankingConfig => {
  const storedCriteria = new Set(stored.criteria.map((entry) => entry.criterion));
  const missing = DEFAULT_RANKING_CONFIG.criteria.filter((entry) => !storedCriteria.has(entry.criterion));
  return missing.length > 0 ? { criteria: [...stored.criteria, ...missing] } : stored;
};

export type AutoAssignmentConfigStoreState = {
  // Only the fields the user changed; unset fields keep following the server defaults.
  policyOverrides: WorkerAutoAssignmentPolicySettings | null;
  rankingConfig: WorkerAssignmentRankingConfig;
};

export type AutoAssignmentConfigStoreActions = {
  updatePolicyConfig: (update: WorkerAutoAssignmentPolicySettings) => void;
  moveRankingCriterion: (index: number, direction: -1 | 1) => void;
  toggleRankingCriterion: (index: number) => void;
  resetConfig: () => void;
};

export const useAutoAssignmentConfigStore = create(
  persist(
    immer<AutoAssignmentConfigStoreState & AutoAssignmentConfigStoreActions>((set, get, store) => {
      return {
        policyOverrides: null,
        rankingConfig: DEFAULT_RANKING_CONFIG,
        updatePolicyConfig: (update) =>
          set((state) => {
            state.policyOverrides = { ...state.policyOverrides, ...update };
          }),
        moveRankingCriterion: (index, direction) =>
          set((state) => {
            const criteria = state.rankingConfig.criteria;
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= criteria.length) {
              return;
            }
            const [item] = criteria.splice(index, 1);
            criteria.splice(targetIndex, 0, item);
          }),
        toggleRankingCriterion: (index) =>
          set((state) => {
            const entry = state.rankingConfig.criteria[index];
            if (entry) {
              entry.enabled = !entry.enabled;
            }
          }),
        resetConfig: () => set(store.getInitialState()),
      };
    }),
    {
      name: 'auto-assignment-config-store',
      version: 1,
      partialize: (state) => {
        return { policyOverrides: state.policyOverrides, rankingConfig: state.rankingConfig };
      },
      merge: (persisted, current) => {
        const parsed = persistedStateSchema.safeParse(persisted);
        if (!parsed.success) {
          return current;
        }
        return {
          ...current,
          policyOverrides: parsed.data.policyOverrides,
          rankingConfig: withNewCriteriaAppended(parsed.data.rankingConfig),
        };
      },
    },
  ),
);

// Stored user overrides merged over server defaults; static fallback until GET auto-assign-workers-defaults responds.
export const useResolvedPolicyConfig = () => {
  const { data: serverDefaults = FALLBACK_AUTO_ASSIGN_DEFAULTS } = useAutoAssignDefaults();
  const policyOverrides = useAutoAssignmentConfigStore((state) => state.policyOverrides);
  const policyConfig: ResolvedPolicyConfig = resolvePolicyConfig(policyOverrides, serverDefaults);

  return { policyConfig, serverDefaults };
};
